/**
 * Checagem de um modelo antes de salvar / enviar a Meta.
 *
 * Devolve uma LISTA DE REGRAS com resultado (e nao so "tem erro"), porque o
 * editor mostra a lista inteira como checklist ao vivo: quem escreve ve o que
 * ja passou e o que falta, na mesma ordem em que a Meta e o motor olham.
 *
 * Duas severidades:
 *  - `erro` impede salvar: a Meta recusaria, ou o motor mandaria texto com
 *    chaves, ou a regra fere uma decisao do dono (so depois do fato, RF08).
 *  - `aviso` nao impede: e risco de reprovacao ou de leitura ruim, com a
 *    explicacao para decidir.
 *
 * Decisoes nao obvias:
 *  1. Variaveis em DOIS estilos: nomeado (`{primeiro_nome}`, o que o motor
 *     entende) e posicional (`{{1}}`, o da Meta). Um estilo por modelo; o
 *     posicional so na API oficial, em sequencia desde `{{1}}`, e cada posicao
 *     diz qual campo do motor a preenche (`variableMap`).
 *  2. "So depois do fato" e checado NO TEXTO, alem do publico: um modelo com
 *     publico "Plano vencido" que diz "seu plano vence amanha" e lembrete
 *     preventivo com outro rotulo. Sem o fato nao ha recuperacao e nao ha
 *     comissao, entao isso e erro, nao aviso.
 *  3. Cupom e desconto: o motor nao gera cupom (RF10 parcial) e a IA e
 *     proibida de inventar. No roteiro de voz e erro; no resto, aviso -- a
 *     empresa pode ter uma condicao real no checkout.
 *  4. As expressoes rodam sobre o texto NORMALIZADO (sem acento, minusculo),
 *     para "Vence amanhã" e "vence amanha" cairem na mesma regra. Cupom,
 *     oferta e insistencia aceitam negacao logo antes ("sem insistir", "nem
 *     cupom"): proibir e exatamente o que o roteiro deve fazer. Lembrete antes
 *     do fato nao aceita negacao.
 */

import { formatCount } from '../../lib/format';
import type { ButtonKind, TemplateAudience, TemplateCategory, TemplateChannel, TemplateSector, VoiceScript } from './types';
import {
  LIMITE_BOTOES,
  LIMITE_CORPO_WHATSAPP,
  LIMITE_PRE_CABECALHO,
  LIMITE_RODAPE,
  LIMITE_ROTULO_BOTAO,
  VARIAVEIS,
  canalPassaPelaMeta,
  canalTemBotoes,
  canalTemRodape,
  normalizarBusca,
  validarNome,
} from './vocabulario';

export type Severidade = 'erro' | 'aviso';

export interface ResultadoRegra {
  readonly id: string;
  /** A regra, escrita como afirmacao ("Texto com até 1.024 caracteres"). */
  readonly titulo: string;
  readonly ok: boolean;
  readonly severidade: Severidade;
  /** Por que falhou e como resolver; `null` quando passou. */
  readonly mensagem: string | null;
  /** Campo do editor que recebe a mensagem; `null` quando e do modelo todo. */
  readonly campo: string | null;
  /** `true` para erro de formato (aparece enquanto digita); `false` para obrigatoriedade (so depois de tentar salvar). */
  readonly imediata: boolean;
}

export interface BotaoParaChecar {
  readonly kind: ButtonKind;
  readonly label: string;
  readonly url: string;
}

export interface EntradaChecagem {
  readonly channel: TemplateChannel;
  readonly category: TemplateCategory;
  readonly sector: TemplateSector;
  readonly audience: TemplateAudience;
  readonly name: string;
  readonly nomesExistentes: readonly string[];
  readonly body: string;
  readonly footer: string;
  readonly subject: string;
  readonly preheader: string;
  readonly buttons: readonly BotaoParaChecar[];
  readonly emailCta: string;
  readonly emailUrl: string;
  readonly voice: VoiceScript;
  readonly variableMap: readonly string[];
}

// -----------------------------------------------------------------------------
// Tokens de variavel
// -----------------------------------------------------------------------------

export type Token =
  | { readonly tipo: 'nomeada'; readonly bruto: string; readonly nome: string; readonly inicio: number; readonly fim: number }
  | { readonly tipo: 'posicional'; readonly bruto: string; readonly posicao: number; readonly inicio: number; readonly fim: number }
  | { readonly tipo: 'malformada'; readonly bruto: string; readonly inicio: number; readonly fim: number; readonly semPar: boolean };

const TOKEN = /\{\{[^{}]*\}\}|\{[^{}]*\}/g;
const NOMEADA = /^\{([a-z_]+)\}$/;
const POSICIONAL = /^\{\{(\d+)\}\}$/;

/** Todo trecho entre chaves do texto, classificado. Chave sem par vira `malformada`. */
export function tokenizar(texto: string): readonly Token[] {
  const tokens: Token[] = [];
  let resto = texto;
  for (const match of texto.matchAll(TOKEN)) {
    const bruto = match[0];
    const inicio = match.index ?? 0;
    const fim = inicio + bruto.length;
    const nomeada = NOMEADA.exec(bruto);
    const posicional = POSICIONAL.exec(bruto);
    if (posicional !== null) {
      tokens.push({ tipo: 'posicional', bruto, posicao: Number(posicional[1] ?? '0'), inicio, fim });
    } else if (nomeada !== null) {
      tokens.push({ tipo: 'nomeada', bruto, nome: nomeada[1] ?? '', inicio, fim });
    } else {
      tokens.push({ tipo: 'malformada', bruto, inicio, fim, semPar: false });
    }
    // Troca por espacos do MESMO tamanho: os indices do que sobrar continuam os do texto original.
    resto = `${resto.slice(0, inicio)}${' '.repeat(bruto.length)}${resto.slice(fim)}`;
  }
  // Chave que sobrou sem par ("Oi {primeiro_nome, tudo bem").
  const sobra = /[{}]/.exec(resto);
  if (sobra !== null) {
    const inicio = sobra.index;
    tokens.push({ tipo: 'malformada', bruto: texto.slice(Math.max(0, inicio - 10), inicio + 11).replace(/\s+/g, ' ').trim(), inicio, fim: inicio + 1, semPar: true });
  }
  return tokens;
}

/** Maior `{{n}}` do conjunto de textos (0 quando nao ha posicional). */
export function maiorPosicao(...textos: readonly string[]): number {
  let maior = 0;
  for (const texto of textos) {
    for (const t of tokenizar(texto)) {
      if (t.tipo === 'posicional' && t.posicao > maior) {
        maior = t.posicao;
      }
    }
  }
  return maior;
}

// -----------------------------------------------------------------------------
// Expressoes de conteudo (texto normalizado)
// -----------------------------------------------------------------------------

/**
 * Lembrete ANTES do fato: o produto nao faz isso (sem fato, sem recuperacao, sem comissao).
 *
 * Dois grupos. `SEMPRE_ANTES` so existe antes do vencimento de um plano
 * ("renove antes", "ainda nao venceu") e e erro em qualquer contexto. `PRAZO`
 * e ambiguo: "vence amanha" e cobranca preventiva quando fala do PLANO, mas e
 * legitimo quando fala de algo gerado DEPOIS do abandono -- o PIX novo que
 * expira em 30 min, a reserva do carrinho, o boleto do checkout abandonado.
 * Sem essa distincao a checagem bloqueava a copy do PIX expirado dizendo que
 * era "lembrete antes do fato".
 */
const SEMPRE_ANTES: readonly RegExp[] = [/\brenove antes\b/, /\brenovar antes\b/, /\bainda nao venceu\b/];

const PRAZO: readonly RegExp[] = [
  /\bvai vencer\b/,
  /\bvence (amanha|hoje|em \d+|na proxima|no proximo|dia \d+)/,
  /\bantes de vencer\b/,
  /\bantes do vencimento\b/,
  /\besta (para|pra) vencer\b/,
  /\bprestes a (vencer|expirar)\b/,
  /\bvai expirar\b/,
  /\bexpira (amanha|hoje|em \d+)/,
  /\bfaltam \d+ dias? para (vencer|o vencimento|expirar)/,
];

/**
 * Quem "vence" ou "expira": a palavra-chave MAIS PROXIMA antes do verbo decide.
 * Coisa gerada depois do fato (PIX, boleto, reserva, link...) libera; plano,
 * assinatura ou mensalidade bloqueia -- "o boleto da mensalidade vence amanha"
 * e cobranca preventiva --, a menos que venha com "vencido(a)": "o PIX para
 * renovar o plano vencido expira em 30 min" fala de um plano que ja venceu.
 */
const SUJEITO_DO_PRAZO = /\b(pix|boleto|reserva|link|codigo|pedido|carrinho|checkout|vagas?|turma|inscricao|condicao|oferta|plano|assinatura|mensalidade)\b( vencid[oa])?/g;
const SUJEITO_DO_PLANO = new Set(['plano', 'assinatura', 'mensalidade']);

function prazoDeAlgoDepoisDoFato(antes: string): boolean {
  let ultimo: RegExpMatchArray | null = null;
  for (const m of antes.matchAll(SUJEITO_DO_PRAZO)) {
    ultimo = m;
  }
  if (ultimo === null) {
    return false;
  }
  return !SUJEITO_DO_PLANO.has(ultimo[1] ?? '') || ultimo[2] !== undefined;
}

const FIM_DE_FRASE = /[.!?;\n]/;

/** O trecho da MESMA frase antes do casamento (ate 60 caracteres). */
function antesNaFrase(normal: string, inicio: number): string {
  let comeco = inicio;
  while (comeco > 0 && inicio - comeco < 60 && !FIM_DE_FRASE.test(normal.charAt(comeco - 1))) {
    comeco -= 1;
  }
  return normal.slice(comeco, inicio);
}

function lembreteAntesDoFato(texto: string): string | null {
  const normal = normalizarBusca(texto);
  for (const padrao of SEMPRE_ANTES) {
    const m = padrao.exec(normal);
    if (m !== null) {
      return m[0];
    }
  }
  for (const padrao of PRAZO) {
    for (const m of normal.matchAll(new RegExp(padrao.source, 'g'))) {
      if (prazoDeAlgoDepoisDoFato(antesNaFrase(normal, m.index ?? 0))) {
        continue;
      }
      return m[0];
    }
  }
  return null;
}

const CUPOM: readonly RegExp[] = [/\bcupo(m|ns)\b/, /\bdesconto\b/, /\d+ ?% ?(off|de desconto)\b/];

const PROMOCIONAL: readonly RegExp[] = [/\boferta\b/, /\bpromocao\b/, /\bmelhor condicao\b/, /\bcondicao especial\b/, /\bso hoje\b/, /\bimperdivel\b/, /\bdesconto\b/];

const INSISTENCIA: readonly RegExp[] = [/\bligue ate atender\b/, /\binsist(a|ir|ente)\b/, /\bligue de novo\b/, /\bligar de novo\b/, /\bvolte a ligar\b/, /\bligue novamente\b/, /\btente ate\b/];

/** "sem insistir", "nem cupom", "nao ofereca desconto": a regra fala do que NAO fazer, e isso e o certo. */
const NEGACAO_ANTES = /\b(sem|nem|nao|nunca|jamais|proibid[oa]s?)\b[^.;:\n]{0,24}$/;

/** "Nao perca o desconto", "nao deixe passar a oferta": aqui o "nao" EMPURRA a oferta, nao a proibe. */
const FALSA_NEGACAO = /\bnao (perca|percam|deixe|deixem|fique sem|esqueca)\b[^.;:\n]{0,24}$/;

function primeiroCasamento(texto: string, padroes: readonly RegExp[], aceitaNegacao = false): string | null {
  const normal = normalizarBusca(texto);
  for (const padrao of padroes) {
    for (const m of normal.matchAll(new RegExp(padrao.source, 'g'))) {
      const antes = normal.slice(Math.max(0, (m.index ?? 0) - 30), m.index ?? 0);
      if (aceitaNegacao && NEGACAO_ANTES.test(antes) && !FALSA_NEGACAO.test(antes)) {
        continue;
      }
      return m[0];
    }
  }
  return null;
}

// -----------------------------------------------------------------------------
// Checagem
// -----------------------------------------------------------------------------

interface TextoDoModelo {
  readonly campo: string;
  readonly rotulo: string;
  readonly texto: string;
}

function textosDo(e: EntradaChecagem): readonly TextoDoModelo[] {
  switch (e.channel) {
    case 'whatsapp_oficial':
    case 'whatsapp_evolution':
      return [
        { campo: 'body', rotulo: 'texto', texto: e.body },
        { campo: 'footer', rotulo: 'rodapé', texto: e.footer },
      ];
    case 'email':
      return [
        { campo: 'subject', rotulo: 'assunto', texto: e.subject },
        { campo: 'preheader', rotulo: 'pré-cabeçalho', texto: e.preheader },
        { campo: 'body', rotulo: 'texto', texto: e.body },
      ];
    case 'voice':
      return [
        { campo: 'opening', rotulo: 'abertura', texto: e.voice.opening },
        { campo: 'objection', rotulo: 'objeções', texto: e.voice.objection },
        { campo: 'closing', rotulo: 'fechamento', texto: e.voice.closing },
      ];
  }
}

function regra(
  id: string,
  titulo: string,
  falha: string | null,
  opcoes: { readonly severidade?: Severidade; readonly campo?: string | null; readonly imediata?: boolean } = {},
): ResultadoRegra {
  return {
    id,
    titulo,
    ok: falha === null,
    severidade: opcoes.severidade ?? 'erro',
    mensagem: falha,
    campo: opcoes.campo ?? null,
    imediata: opcoes.imediata ?? true,
  };
}

const NOMES_DO_MOTOR = new Set(VARIAVEIS.map((v) => v.name));

export function checarModelo(e: EntradaChecagem): readonly ResultadoRegra[] {
  const regras: ResultadoRegra[] = [];
  const meta = canalPassaPelaMeta(e.channel);
  const textos = textosDo(e);
  const urls = canalTemBotoes(e.channel) ? e.buttons.filter((b) => b.kind === 'link').map((b) => b.url) : e.channel === 'email' && e.emailCta.trim() !== '' ? [e.emailUrl] : [];
  // Rotulo de botao tambem e texto que o lead le: "Renove antes" ou "Usar cupom" num botao contam igual.
  const rotulos = canalTemBotoes(e.channel) ? e.buttons.map((b) => b.label) : e.channel === 'email' ? [e.emailCta] : [];
  const tudo = [...textos.map((t) => t.texto), ...rotulos, ...urls];

  // 1. Nome
  regras.push(regra('nome', 'Nome no formato da Meta (minúsculas, números e _), sem repetir', validarNome(e.name, e.nomesExistentes), { campo: 'name', imediata: e.name !== '' }));

  // 2. Conteudo obrigatorio
  switch (e.channel) {
    case 'whatsapp_oficial':
    case 'whatsapp_evolution':
      regras.push(regra('conteudo', 'Texto da mensagem preenchido', e.body.trim() === '' ? 'Escreva o texto da mensagem.' : null, { campo: 'body', imediata: false }));
      regras.push(
        regra(
          'limite-corpo',
          `Texto com até ${formatCount(LIMITE_CORPO_WHATSAPP)} caracteres`,
          e.body.length <= LIMITE_CORPO_WHATSAPP
            ? null
            : meta
              ? `O texto tem ${formatCount(e.body.length)} caracteres; a Meta aceita até ${formatCount(LIMITE_CORPO_WHATSAPP)} no corpo do modelo. Corte ${formatCount(e.body.length - LIMITE_CORPO_WHATSAPP)}.`
              : `O texto tem ${formatCount(e.body.length)} caracteres. Texto livre pela Evolution aceita mais, mas mensagem longa no WhatsApp é pouco lida: tente ficar em ${formatCount(LIMITE_CORPO_WHATSAPP)}.`,
          // O limite de 1.024 e da Meta (corpo do modelo). Na Evolution o WhatsApp aceita bem mais: la e aviso, nao bloqueio.
          { campo: 'body', severidade: meta ? 'erro' : 'aviso' },
        ),
      );
      break;
    case 'email':
      regras.push(regra('assunto', 'Assunto preenchido', e.subject.trim() === '' ? 'Escreva o assunto: é o que decide se o e-mail é aberto.' : null, { campo: 'subject', imediata: false }));
      regras.push(regra('conteudo', 'Texto do e-mail preenchido', e.body.trim() === '' ? 'Escreva o texto do e-mail.' : null, { campo: 'body', imediata: false }));
      regras.push(
        regra(
          'pre-cabecalho',
          `Pré-cabeçalho com até ${LIMITE_PRE_CABECALHO} caracteres`,
          e.preheader.length > LIMITE_PRE_CABECALHO ? `A caixa de entrada corta depois de ~${LIMITE_PRE_CABECALHO} caracteres; o seu tem ${e.preheader.length}.` : null,
          { severidade: 'aviso', campo: 'preheader' },
        ),
      );
      break;
    case 'voice':
      regras.push(regra('abertura', 'Abertura da ligação preenchida', e.voice.opening.trim() === '' ? 'Escreva como a IA abre a ligação.' : null, { campo: 'opening', imediata: false }));
      regras.push(regra('fechamento', 'Fechamento preenchido', e.voice.closing.trim() === '' ? 'Escreva como a IA encerra e qual é o próximo passo.' : null, { campo: 'closing', imediata: false }));
      break;
  }

  // 3. Rodape
  if (canalTemRodape(e.channel)) {
    const rodapeLongo = e.footer.length > LIMITE_RODAPE ? `O rodapé tem ${e.footer.length} caracteres; o limite é ${LIMITE_RODAPE}.` : null;
    const rodapeComVariavel = meta && tokenizar(e.footer).length > 0 ? 'A Meta não aceita variável no rodapé: deixe só texto fixo (ex.: "Responda SAIR para não receber mais").' : null;
    regras.push(regra('rodape', meta ? `Rodapé com até ${LIMITE_RODAPE} caracteres e sem variável` : `Rodapé com até ${LIMITE_RODAPE} caracteres`, rodapeLongo ?? rodapeComVariavel, { campo: 'footer' }));
  }

  // 4. Botoes (API oficial)
  if (canalTemBotoes(e.channel)) {
    regras.push(regra('botoes-quantidade', `Até ${LIMITE_BOTOES} botões nesta prévia`, e.buttons.length > LIMITE_BOTOES ? `São ${e.buttons.length} botões; esta prévia aceita até ${LIMITE_BOTOES}.` : null));
    const rotulosNormais = e.buttons.map((b) => normalizarBusca(b.label));
    e.buttons.forEach((b, i) => {
      const n = i + 1;
      const label = b.label.trim();
      let falha: string | null = null;
      let imediata = true;
      if (label === '') {
        falha = `Dê um texto ao botão ${n}.`;
        imediata = false;
      } else if (b.label.length > LIMITE_ROTULO_BOTAO) {
        falha = `"${b.label}" tem ${b.label.length} caracteres; o limite é ${LIMITE_ROTULO_BOTAO}.`;
      } else if (tokenizar(b.label).length > 0) {
        falha = 'A Meta não aceita variável no texto do botão.';
      } else if (rotulosNormais.indexOf(normalizarBusca(b.label)) !== i) {
        falha = 'Dois botões com o mesmo texto: o lead não sabe qual tocar e a Meta recusa.';
      }
      regras.push(regra(`botao-${i}-rotulo`, `Botão ${n}: texto com até ${LIMITE_ROTULO_BOTAO} caracteres, sem variável e sem repetir`, falha, { campo: `button-${i}-label`, imediata }));
      if (b.kind === 'link') {
        regras.push(regra(`botao-${i}-url`, `Botão ${n}: link para o checkout`, falhaDeUrl(b.url), { campo: `button-${i}-url` }));
      }
    });
  }
  if (e.channel === 'email' && e.emailCta.trim() !== '') {
    regras.push(regra('email-url', 'Botão do e-mail: link para o checkout', falhaDeUrl(e.emailUrl), { campo: 'emailUrl' }));
  }

  // 5. Variaveis
  const tokensPorTexto = textos.map((t) => ({ ...t, tokens: tokenizar(t.texto) }));
  const todosTokens = tokensPorTexto.flatMap((t) => t.tokens.map((tk) => ({ campo: t.campo, rotulo: t.rotulo, token: tk })));

  const malformada = todosTokens.find((t) => t.token.tipo === 'malformada');
  regras.push(
    regra(
      'variaveis-formato',
      'Variáveis escritas do jeito certo',
      malformada === undefined
        ? null
        : malformada.token.tipo === 'malformada' && malformada.token.semPar
          ? `Chave sem par no ${malformada.rotulo}, perto de “${malformada.token.bruto}”. Feche a variável, como {primeiro_nome}, ou apague a chave solta.`
          : `"${malformada.token.bruto}" no ${malformada.rotulo} não é uma variável válida. Use minúsculas sem espaço entre uma chave, como {primeiro_nome}${meta ? ', ou o número entre duas chaves, como {{1}}' : ''}.`,
      { campo: malformada?.campo ?? null },
    ),
  );

  const desconhecida = todosTokens.find((t) => t.token.tipo === 'nomeada' && !NOMES_DO_MOTOR.has(t.token.nome));
  regras.push(
    regra(
      'variaveis-motor',
      'Só variáveis que o motor preenche',
      desconhecida === undefined ? null : `${desconhecida.token.bruto} não existe no motor: sairia com as chaves no texto. Use uma das variáveis da lista.`,
      { campo: desconhecida?.campo ?? null },
    ),
  );

  const usaVendedora = todosTokens.some((t) => t.token.tipo === 'nomeada' && t.token.nome === 'vendedora');
  if (usaVendedora) {
    regras.push(
      regra(
        'variavel-vendedora',
        '{vendedora} só com quem já está com uma vendedora',
        e.audience === 'respondeu_regua' ? null : '{vendedora} só é preenchida para quem respondeu à régua e caiu com uma vendedora. Para outro público ela sairia vazia.',
        { severidade: 'aviso' },
      ),
    );
  }

  const posicionais = todosTokens.filter((t) => t.token.tipo === 'posicional');
  const nomeadas = todosTokens.filter((t) => t.token.tipo === 'nomeada');
  if (posicionais.length > 0) {
    const primeira = posicionais[0];
    if (!meta) {
      regras.push(regra('estilo', 'Variáveis no estilo do canal', `${primeira?.token.bruto ?? '{{1}}'} só existe na API oficial da Meta. Neste canal use o nome, como {primeiro_nome}.`, { campo: primeira?.campo ?? null }));
    } else {
      regras.push(
        regra(
          'estilo',
          'Um estilo de variável por modelo',
          nomeadas.length > 0 ? `Não misture ${primeira?.token.bruto ?? '{{1}}'} com ${nomeadas[0]?.token.bruto ?? '{primeiro_nome}'}: a Meta aceita um estilo por modelo. Escolha números ou nomes.` : null,
          { campo: primeira?.campo ?? null },
        ),
      );
      const numeros = Array.from(new Set(posicionais.map((p) => (p.token.tipo === 'posicional' ? p.token.posicao : 0)))).sort((a, b) => a - b);
      const maior = numeros[numeros.length - 1] ?? 0;
      const faltando: number[] = [];
      for (let n = 1; n <= maior; n += 1) {
        if (!numeros.includes(n)) {
          faltando.push(n);
        }
      }
      regras.push(
        regra(
          'sequencia',
          'Variáveis numeradas em sequência a partir de {{1}}',
          numeros.includes(0)
            ? 'A contagem começa em {{1}}, não em {{0}}.'
            : faltando.length > 0
              ? `Falta ${faltando.map((n) => `{{${n}}}`).join(', ')}: a Meta exige {{1}}, {{2}}, {{3}}... sem pular número.`
              : null,
          { campo: primeira?.campo ?? null },
        ),
      );
      const semMapa: number[] = [];
      for (let n = 1; n <= maior; n += 1) {
        const nome = e.variableMap[n - 1];
        if (nome === undefined || nome === '' || !NOMES_DO_MOTOR.has(nome)) {
          semMapa.push(n);
        }
      }
      regras.push(
        regra(
          'mapa',
          'Cada {{n}} diz o que o motor coloca ali',
          semMapa.length > 0 ? `Escolha o que preenche ${semMapa.map((n) => `{{${n}}}`).join(', ')} (primeiro nome, produto, link...). Sem isso a mensagem sai com o número.` : null,
          { campo: 'variableMap', imediata: false },
        ),
      );
    }
  }

  if (meta) {
    const corpo = e.body.trim();
    const tokensCorpo = tokenizar(corpo).filter((t) => t.tipo !== 'malformada');
    const primeiro = tokensCorpo[0];
    const ultimo = tokensCorpo[tokensCorpo.length - 1];
    const naBorda = corpo !== '' && ((primeiro !== undefined && primeiro.inicio === 0) || (ultimo !== undefined && corpo.slice(ultimo.fim).replace(/[.!?\s]/g, '') === ''));
    regras.push(
      regra(
        'bordas',
        'Texto não começa nem termina com variável',
        naBorda ? 'A Meta costuma recusar texto que abre ou fecha com variável. Ponha uma palavra antes (ex.: "Oi, {primeiro_nome}!") ou depois.' : null,
        { severidade: 'aviso', campo: 'body' },
      ),
    );
    let coladas = false;
    for (let i = 1; i < tokensCorpo.length; i += 1) {
      const anterior = tokensCorpo[i - 1];
      const atual = tokensCorpo[i];
      if (anterior !== undefined && atual !== undefined && corpo.slice(anterior.fim, atual.inicio).trim() === '') {
        coladas = true;
      }
    }
    regras.push(regra('coladas', 'Nenhuma variável colada na outra', coladas ? 'Duas variáveis seguidas sem texto entre elas são motivo comum de recusa. Separe com uma palavra.' : null, { severidade: 'aviso', campo: 'body' }));
  }

  // 6. Regras de negocio
  const trechoAntes = tudo.map((t) => lembreteAntesDoFato(t)).find((t) => t !== null) ?? null;
  regras.push(
    regra(
      'depois-do-fato',
      'Fala com quem já abandonou ou já venceu',
      trechoAntes === null
        ? null
        : `"${trechoAntes}" é lembrete antes do fato. O Converte Fácil só fala com quem já abandonou o checkout ou já teve o plano vencido: sem o fato não há recuperação nem comissão.`,
      { campo: textos[0]?.campo ?? null },
    ),
  );

  const trechoCupom = tudo.map((t) => primeiroCasamento(t, CUPOM, true)).find((t) => t !== null) ?? null;
  regras.push(
    regra(
      'sem-cupom',
      e.channel === 'voice' ? 'A IA não promete cupom nem desconto' : 'Sem cupom ou desconto que o motor não dá',
      trechoCupom === null
        ? null
        : e.channel === 'voice'
          ? `"${trechoCupom}": a IA é proibida de oferecer cupom ou desconto. Fale de parcelamento e do que está incluso.`
          : `"${trechoCupom}": o motor não gera cupom. Só prometa uma condição que a empresa já oferece no checkout.`,
      { severidade: e.channel === 'voice' ? 'erro' : 'aviso' },
    ),
  );

  if (meta) {
    const trechoPromo = e.category === 'utility' ? primeiroCasamento([e.body, e.footer, ...rotulos].join('\n'), PROMOCIONAL, true) : null;
    const marketingNaRegua = e.category === 'marketing' && e.sector === 'funil';
    regras.push(
      regra(
        'categoria',
        'Categoria coerente com o texto',
        trechoPromo !== null
          ? `"${trechoPromo}" é linguagem de oferta: a Meta reclassifica ou recusa utility que promete vantagem. Troque a palavra ou envie como marketing.`
          : marketingNaRegua
            ? 'A régua usa utility. Marketing só sai para quem deu opt-in e passa por análise mais rígida.'
            : null,
        { severidade: 'aviso' },
      ),
    );
  }

  if (e.channel === 'voice') {
    const lerLink = [e.voice.opening, e.voice.objection, e.voice.closing].some((t) => t.includes('{link}') || /https?:\/\//.test(t));
    regras.push(
      regra(
        'voz-link',
        'A IA não lê endereço em voz alta',
        lerLink ? 'O roteiro tem {link} ou um endereço: a IA leria a URL pelo telefone. Diga que o link vai pelo WhatsApp.' : null,
        { severidade: 'aviso', campo: 'closing' },
      ),
    );
    const trechoInsistir = [e.voice.opening, e.voice.objection, e.voice.closing].map((t) => primeiroCasamento(t, INSISTENCIA, true)).find((t) => t !== null) ?? null;
    regras.push(
      regra(
        'rf08',
        'Roteiro respeita o teto de 2 ligações',
        trechoInsistir === null ? null : `"${trechoInsistir}": o motor liga no máximo duas vezes por lead (RF08). O roteiro não pode pedir para insistir.`,
        { campo: 'objection' },
      ),
    );
  }

  return regras;
}

function falhaDeUrl(url: string): string | null {
  const limpa = url.trim();
  if (limpa === '') {
    return 'Informe o link. Use {link} para o checkout do próprio lead, com a marca da régua.';
  }
  if (limpa === '{link}') {
    return null;
  }
  if (!limpa.startsWith('https://')) {
    return 'O link precisa começar com https:// ou ser {link} (o checkout do lead, com a marca da régua que mede o clique).';
  }
  const tokens = tokenizar(limpa);
  if (tokens.length > 1) {
    return 'A Meta aceita no máximo uma variável no link, e só no fim.';
  }
  const unico = tokens[0];
  if (unico !== undefined && unico.tipo === 'malformada') {
    return `"${unico.bruto}" no link não é uma variável válida. Use {link} ou uma variável da lista.`;
  }
  if (unico !== undefined && unico.tipo === 'nomeada' && !NOMES_DO_MOTOR.has(unico.nome)) {
    return `${unico.bruto} não existe no motor: o link sairia com as chaves e quebraria.`;
  }
  if (unico !== undefined && unico.fim !== limpa.length) {
    return 'A variável do link precisa ficar no fim do endereço.';
  }
  return null;
}

/** Primeira mensagem de ERRO por campo, para o `Field`. */
export function errosPorCampo(regras: readonly ResultadoRegra[], mostrarTodas: boolean): Readonly<Record<string, string>> {
  const erros: Record<string, string> = {};
  for (const r of regras) {
    if (r.ok || r.severidade !== 'erro' || r.campo === null || r.mensagem === null) {
      continue;
    }
    if (!mostrarTodas && !r.imediata) {
      continue;
    }
    if (erros[r.campo] === undefined) {
      erros[r.campo] = r.mensagem;
    }
  }
  return erros;
}

export function contarFalhas(regras: readonly ResultadoRegra[]): { readonly erros: number; readonly avisos: number; readonly ok: number } {
  let erros = 0;
  let avisos = 0;
  let ok = 0;
  for (const r of regras) {
    if (r.ok) {
      ok += 1;
    } else if (r.severidade === 'erro') {
      erros += 1;
    } else {
      avisos += 1;
    }
  }
  return { erros, avisos, ok };
}
